import { useMemo } from 'react';
import type { Submission, SubmissionStatus } from '../../hooks/useSubmissions';
import { STATUS_VALUES, STATUS_LABELS } from '../../hooks/useSubmissions';
import type { GeneratorConfigV1 } from '../../types/generator-config';
import { formatPrice } from '../../lib/pricing';

interface SubmissionDetailProps {
    submission: Submission;
    config: GeneratorConfigV1 | null;
    currency: string;
    onClose: () => void;
    onUpdateStatus: (status: SubmissionStatus) => void;
}

export function SubmissionDetail({
    submission,
    config,
    currency,
    onClose,
    onUpdateStatus,
}: SubmissionDetailProps) {
    const answers = submission.payload as Record<string, unknown>;

    const fieldsById = useMemo(() => {
        const map: Record<string, { label: string; options?: { id: string; label: string }[] }> = {};
        if (!config) return map;
        for (const step of config.steps) {
            for (const f of step.fields) {
                map[f.id] = {
                    label: f.label,
                    options: 'options' in f ? (f as { options: { id: string; label: string }[] }).options : undefined,
                };
            }
        }
        return map;
    }, [config]);

    const createdAt = new Date(submission.created_at).toLocaleString('fr-FR', {
        dateStyle: 'long',
        timeStyle: 'short',
    });

    return (
        <div className="sub-drawer" role="dialog" aria-modal="true">
            <div className="sub-drawer-backdrop" onClick={onClose} />
            <div className="sub-drawer-panel">
                <header className="sub-drawer-head">
                    <div>
                        <div className="sub-drawer-eyebrow">Demande reçue le {createdAt}</div>
                        <h2>{submission.contact_name || 'Contact sans nom'}</h2>
                    </div>
                    <button type="button" className="icon-btn" onClick={onClose} aria-label="Fermer">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </header>

                <div className="sub-drawer-body">
                    <section className="sub-block">
                        <h3>Contact</h3>
                        <dl className="sub-dl">
                            {submission.contact_email && (
                                <>
                                    <dt>Email</dt>
                                    <dd>
                                        <a href={`mailto:${submission.contact_email}`}>{submission.contact_email}</a>
                                    </dd>
                                </>
                            )}
                            {submission.contact_phone && (
                                <>
                                    <dt>Téléphone</dt>
                                    <dd>
                                        <a href={`tel:${submission.contact_phone}`}>{submission.contact_phone}</a>
                                    </dd>
                                </>
                            )}
                            {submission.source && (
                                <>
                                    <dt>Source</dt>
                                    <dd className="sub-source" title={submission.source}>{submission.source}</dd>
                                </>
                            )}
                        </dl>
                    </section>

                    <section className="sub-block">
                        <div className="sub-status-row">
                            <h3>Statut</h3>
                            <select
                                value={submission.status}
                                onChange={(e) => onUpdateStatus(e.target.value as SubmissionStatus)}
                                className={`sub-status-select sub-status-${submission.status}`}
                            >
                                {STATUS_VALUES.map((s) => (
                                    <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                                ))}
                            </select>
                        </div>
                    </section>

                    {submission.estimated_total !== null && (
                        <section className="sub-block sub-total">
                            <div>
                                <span className="sub-total-label">Estimation affichée au client</span>
                                <span className="sub-total-amount">
                                    {formatPrice(submission.estimated_total, currency)}
                                </span>
                            </div>
                        </section>
                    )}

                    <section className="sub-block">
                        <h3>Réponses</h3>
                        <dl className="sub-dl sub-answers">
                            {Object.keys(answers).length === 0 && <p className="sub-empty">Aucune réponse.</p>}
                            {Object.entries(answers).map(([id, value]) => {
                                const f = fieldsById[id];
                                const label = f?.label ?? id;
                                const displayed = formatAnswerValue(value, f?.options);
                                return (
                                    <div key={id} className="sub-row">
                                        <dt>{label}</dt>
                                        <dd>{displayed}</dd>
                                    </div>
                                );
                            })}
                        </dl>
                    </section>

                    {typeof answers.message === 'string' && answers.message && (
                        <section className="sub-block">
                            <h3>Message libre</h3>
                            <p className="sub-message">{answers.message as string}</p>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
}

function formatAnswerValue(value: unknown, options?: { id: string; label: string }[]): string {
    if (value === null || value === undefined || value === '') return '—';
    if (Array.isArray(value)) {
        if (options) {
            return value
                .map((v) => options.find((o) => o.id === v)?.label ?? String(v))
                .join(', ');
        }
        return value.map(String).join(', ');
    }
    if (typeof value === 'string' && options) {
        return options.find((o) => o.id === value)?.label ?? value;
    }
    if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
    return String(value);
}
